// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - ArtifactTasksInteractif
// Affichage enrichi et interactif des tâches dans le panneau Artifacts
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ListTodo, Check, Circle, Plus, Trash2, Calendar, Flag,
  Clock, ChevronDown, ChevronUp, Edit3, Save, X, Loader2,
  AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer l'userId
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  
  if (ecosystemUser) {
    try { return JSON.parse(ecosystemUser).id; } catch(e) {}
  }
  if (buyerUser) {
    try { return JSON.parse(buyerUser).id; } catch(e) {}
  }
  if (clientUser) {
    try { return JSON.parse(clientUser).id; } catch(e) {}
  }
  return localStorage.getItem('userId');
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function ArtifactTasksInteractif({ data, onUpdate }) {
  const [tasks, setTasks] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, completed
  
  // Initialiser avec les données
  useEffect(() => {
    if (data) {
      const taskList = Array.isArray(data) ? data : (data.tasks || [data]);
      const normalized = taskList.map((task, index) => ({
        id: task.id || `task-${Date.now()}-${index}`,
        title: task.title || task.titre || task.description || 'Tâche',
        description: task.description || task.details || '',
        completed: task.completed || task.status === 'done' || task.status === 'completed',
        priority: task.priority || task.priorite || 'normal', // low, normal, high
        due_date: task.due_date || task.echeance || null,
        created_at: task.created_at || new Date().toISOString()
      }));
      setTasks(normalized);
    }
  }, [data]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Sauvegarder les tâches dans le backend
  const saveTasks = async (tasksToSave) => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      await fetch(`${API_URL}/api/artifacts/tasks/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tasks: tasksToSave.map(t => ({
            id: t.id,
            title: t.title,
            description: t.description || '',
            completed: t.completed,
            priority: t.priority,
            due_date: t.due_date
          }))
        })
      });
    } catch (err) {
      console.error('Error saving tasks:', err);
    }
  };
  
  const toggleTask = (taskId) => {
    const newTasks = tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    setTasks(newTasks);
    saveTasks(newTasks);
  };
  
  const deleteTask = async (taskId) => {
    const userId = getUserId();
    
    // Supprimer localement
    setTasks(prev => prev.filter(task => task.id !== taskId));
    toast.success('Tâche supprimée');
    
    // Supprimer dans le backend
    if (userId) {
      try {
        await fetch(`${API_URL}/api/artifacts/tasks/${taskId}?user_id=${userId}`, {
          method: 'DELETE'
        });
      } catch (err) {
        console.error('Error deleting task:', err);
      }
    }
  };
  
  const addTask = () => {
    if (!newTaskTitle.trim()) return;
    
    const newTask = {
      id: `task-${Date.now()}`,
      title: newTaskTitle.trim(),
      description: '',
      completed: false,
      priority: 'normal',
      due_date: null,
      created_at: new Date().toISOString()
    };
    
    const newTasks = [newTask, ...tasks];
    setTasks(newTasks);
    setNewTaskTitle('');
    toast.success('Tâche ajoutée');
    
    // Sauvegarder
    saveTasks([newTask]);
  };
  
  const updateTaskPriority = (taskId, priority) => {
    const newTasks = tasks.map(task =>
      task.id === taskId ? { ...task, priority } : task
    );
    setTasks(newTasks);
    saveTasks(newTasks.filter(t => t.id === taskId));
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FILTRAGE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const filteredTasks = tasks.filter(task => {
    if (filter === 'pending') return !task.completed;
    if (filter === 'completed') return task.completed;
    return true;
  });
  
  const completedCount = tasks.filter(t => t.completed).length;
  const pendingCount = tasks.filter(t => !t.completed).length;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  const priorityConfig = {
    low: { color: 'text-gray-400', bg: 'bg-gray-100', label: 'Faible' },
    normal: { color: 'text-blue-500', bg: 'bg-blue-100', label: 'Normal' },
    high: { color: 'text-red-500', bg: 'bg-red-100', label: 'Urgent' }
  };
  
  return (
    <div className="h-full flex flex-col bg-white rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3 text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ListTodo className="w-5 h-5" />
            <span className="font-semibold">Mes Tâches</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="px-2 py-0.5 bg-white/20 rounded-full">{pendingCount} en cours</span>
            <span className="px-2 py-0.5 bg-white/20 rounded-full">{completedCount} terminées</span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: tasks.length ? `${(completedCount / tasks.length) * 100}%` : '0%' }}
          />
        </div>
      </div>
      
      {/* Filtres */}
      <div className="flex gap-1 p-2 border-b border-gray-100">
        {[
          { id: 'all', label: 'Toutes' },
          { id: 'pending', label: 'En cours' },
          { id: 'completed', label: 'Terminées' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`
              flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors
              ${filter === f.id 
                ? 'bg-purple-100 text-purple-700' 
                : 'text-gray-500 hover:bg-gray-100'
              }
            `}
          >
            {f.label}
          </button>
        ))}
      </div>
      
      {/* Ajouter une tâche */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex gap-2">
          <input
            type="text"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
            placeholder="Nouvelle tâche..."
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
          />
          <button
            onClick={addTask}
            disabled={!newTaskTitle.trim()}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Liste des tâches */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <AnimatePresence>
          {filteredTasks.map((task) => {
            const priority = priorityConfig[task.priority] || priorityConfig.normal;
            
            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                className={`
                  group flex items-start gap-3 p-3 rounded-xl border transition-all
                  ${task.completed 
                    ? 'bg-gray-50 border-gray-100' 
                    : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-sm'
                  }
                `}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleTask(task.id)}
                  className={`
                    flex-shrink-0 w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center transition-all
                    ${task.completed 
                      ? 'bg-emerald-500 border-emerald-500 text-white' 
                      : 'border-gray-300 hover:border-purple-500'
                    }
                  `}
                >
                  {task.completed && <Check className="w-3 h-3" />}
                </button>
                
                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${task.completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                    {task.title}
                  </p>
                  
                  {task.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{task.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 mt-2">
                    {/* Priorité */}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${priority.bg} ${priority.color}`}>
                      {priority.label}
                    </span>
                    
                    {/* Date d'échéance */}
                    {task.due_date && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {new Date(task.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Changer priorité */}
                  <div className="flex gap-0.5">
                    {['low', 'normal', 'high'].map((p) => (
                      <button
                        key={p}
                        onClick={() => updateTaskPriority(task.id, p)}
                        className={`
                          w-5 h-5 rounded flex items-center justify-center transition-colors
                          ${task.priority === p ? priorityConfig[p].bg : 'hover:bg-gray-100'}
                        `}
                        title={priorityConfig[p].label}
                      >
                        <Flag className={`w-3 h-3 ${task.priority === p ? priorityConfig[p].color : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                  
                  {/* Supprimer */}
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        
        {filteredTasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">
              {filter === 'completed' ? 'Aucune tâche terminée' :
               filter === 'pending' ? 'Toutes les tâches sont terminées !' :
               'Aucune tâche'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
